/**
 * Demo fonts, zod-free so the player can import it (`@mado/schema/fonts`)
 * without pulling zod into its budget.
 *
 * A demo's `theme.font` is either absent (the viewer's system font stack —
 * every document published before fonts existed renders that way), one of the
 * library fonts below, or a font the creator uploaded (every plan). Library font
 * files are self-hosted with the player (`/p/fonts/…`), never loaded from
 * Google: a request to fonts.gstatic.com would send each viewer's IP to a
 * third party, which breaks the no-consent-banner promise (CLAUDE.md rule 3).
 *
 * All library fonts are open-licence (SIL OFL 1.1 or Apache 2.0); licence
 * texts ship next to the files.
 */

export type FontCategory = 'sans' | 'display' | 'serif' | 'rounded' | 'mono';

export interface LibraryFont {
  /** Stable id stored in documents. Never rename or remove one. */
  id: string;
  /** CSS / fontconfig family name. */
  family: string;
  category: FontCategory;
  /** Generic fallback, used while the file loads and if it fails. */
  fallback: 'sans-serif' | 'serif' | 'monospace';
}

/**
 * The library, in picker order. Ids are forever (a published document stores
 * them): add new ones at the end of a category, never rename or remove.
 */
export const LIBRARY_FONTS = [
  { id: 'inter', family: 'Inter', category: 'sans', fallback: 'sans-serif' },
  { id: 'ibm-plex-sans', family: 'IBM Plex Sans', category: 'sans', fallback: 'sans-serif' },
  { id: 'work-sans', family: 'Work Sans', category: 'sans', fallback: 'sans-serif' },
  { id: 'dm-sans', family: 'DM Sans', category: 'sans', fallback: 'sans-serif' },
  { id: 'manrope', family: 'Manrope', category: 'sans', fallback: 'sans-serif' },
  {
    id: 'plus-jakarta-sans',
    family: 'Plus Jakarta Sans',
    category: 'sans',
    fallback: 'sans-serif',
  },
  { id: 'figtree', family: 'Figtree', category: 'sans', fallback: 'sans-serif' },
  { id: 'outfit', family: 'Outfit', category: 'sans', fallback: 'sans-serif' },
  { id: 'nunito-sans', family: 'Nunito Sans', category: 'sans', fallback: 'sans-serif' },
  { id: 'source-sans-3', family: 'Source Sans 3', category: 'sans', fallback: 'sans-serif' },
  { id: 'rubik', family: 'Rubik', category: 'sans', fallback: 'sans-serif' },
  { id: 'space-grotesk', family: 'Space Grotesk', category: 'display', fallback: 'sans-serif' },
  { id: 'poppins', family: 'Poppins', category: 'display', fallback: 'sans-serif' },
  { id: 'montserrat', family: 'Montserrat', category: 'display', fallback: 'sans-serif' },
  { id: 'sora', family: 'Sora', category: 'display', fallback: 'sans-serif' },
  { id: 'lora', family: 'Lora', category: 'serif', fallback: 'serif' },
  { id: 'merriweather', family: 'Merriweather', category: 'serif', fallback: 'serif' },
  { id: 'source-serif-4', family: 'Source Serif 4', category: 'serif', fallback: 'serif' },
  { id: 'fraunces', family: 'Fraunces', category: 'serif', fallback: 'serif' },
  { id: 'playfair-display', family: 'Playfair Display', category: 'serif', fallback: 'serif' },
  { id: 'nunito', family: 'Nunito', category: 'rounded', fallback: 'sans-serif' },
  { id: 'quicksand', family: 'Quicksand', category: 'rounded', fallback: 'sans-serif' },
  { id: 'ibm-plex-mono', family: 'IBM Plex Mono', category: 'mono', fallback: 'monospace' },
  // Added 2026-10-04 so "match my website" can land on the fonts product sites
  // actually use (Geist first: shadcn/Next sites ship it).
  { id: 'geist', family: 'Geist', category: 'sans', fallback: 'sans-serif' },
  { id: 'open-sans', family: 'Open Sans', category: 'sans', fallback: 'sans-serif' },
  { id: 'public-sans', family: 'Public Sans', category: 'sans', fallback: 'sans-serif' },
  { id: 'instrument-sans', family: 'Instrument Sans', category: 'sans', fallback: 'sans-serif' },
  { id: 'onest', family: 'Onest', category: 'sans', fallback: 'sans-serif' },
  { id: 'lexend', family: 'Lexend', category: 'sans', fallback: 'sans-serif' },
  { id: 'mulish', family: 'Mulish', category: 'sans', fallback: 'sans-serif' },
  { id: 'karla', family: 'Karla', category: 'sans', fallback: 'sans-serif' },
  { id: 'geist-mono', family: 'Geist Mono', category: 'mono', fallback: 'monospace' },
] as const satisfies readonly LibraryFont[];

export type LibraryFontId = (typeof LIBRARY_FONTS)[number]['id'];

export const LIBRARY_FONT_IDS = LIBRARY_FONTS.map((f) => f.id) as [
  LibraryFontId,
  ...LibraryFontId[],
];

/** Weights shipped for every library font — what the player and exporter draw
 *  with (body 400, tooltips 500, buttons/headings 600; the exporter's 650
 *  resolves to 600). */
export const LIBRARY_FONT_WEIGHTS = [400, 500, 600] as const;

/** The player's font when `theme.font` is absent: the viewer's own OS font. */
export const SYSTEM_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

export function libraryFont(id: string): LibraryFont | undefined {
  return (LIBRARY_FONTS as readonly LibraryFont[]).find((f) => f.id === id);
}

/** Root-relative URL of a library font's stylesheet (its `@font-face` rules,
 *  one per weight and script subset, from `packages/fonts`), served with the
 *  player. Resolved against the page origin like every `/a/…` asset — never a
 *  baked-in host. */
export function libraryFontCssUrl(id: LibraryFontId): string {
  return `/p/fonts/${id}.css`;
}

/** Family name a custom (uploaded) font is registered under. Namespaced by
 *  hash so it can never collide with a library or system family. */
export function customFontFamily(hash: string): string {
  return `mado-custom-${hash.slice(0, 12)}`;
}

/** Only the parts of `theme.font` this module needs — structurally matches
 *  the zod-inferred type in demo.ts without importing zod. */
export type ThemeFontLike =
  | { kind: 'library'; id: string }
  | { kind: 'custom'; name: string; hash: string; faces: readonly { weight: string }[] }
  | undefined
  | null;

/** CSS `font-family` value for a theme font: the chosen family first, then a
 *  fallback, so text is always readable while (or if) the file fails to load. */
export function fontFamilyStack(font: ThemeFontLike): string {
  if (!font) return SYSTEM_FONT_STACK;
  if (font.kind === 'library') {
    const f = libraryFont(font.id);
    if (!f) return SYSTEM_FONT_STACK;
    return `"${f.family}", ${f.fallback === 'sans-serif' ? SYSTEM_FONT_STACK : f.fallback}`;
  }
  return `"${customFontFamily(font.hash)}", ${SYSTEM_FONT_STACK}`;
}
